import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DataTableColumn, DataTableRow } from './types';
import { getAlign, getSize } from './utils';
import Icon from '../icon';

export type DraggableTableRowProps = {
  columns: DataTableColumn[];
  row: DataTableRow;
  index: number;
  stickyFirstColumn?: boolean;
  onRowClick?: (row: DataTableRow) => void;
  selectedRowId?: string | null;
  onChangeOrder: (sourceId: string, targetId: string) => void;
  onDrop: () => void;
  dragType: string;
  dragEnabled: boolean;
  isFirstRow?: boolean;
  isLastRow?: boolean;
};

type DragItem = {
  id: string;
  index: number;
};

const DraggableTableRow = ({
  columns,
  row,
  index,
  stickyFirstColumn = false,
  onRowClick,
  selectedRowId,
  onChangeOrder,
  onDrop,
  dragType,
  dragEnabled,
  isFirstRow = false,
  isLastRow = false,
}: DraggableTableRowProps) => {
  const isSelected =
    selectedRowId != null &&
    row.id != null &&
    String(row.id) === String(selectedRowId);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: dragType,
    item: (): DragItem => ({ id: row.id, index }),
    isDragging: (monitor) => row.id === monitor.getItem().id,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: dragType,
    hover: (source: DragItem) => {
      if (source.id === row.id) return;
      onChangeOrder(source.id, row.id);
    },
    drop: () => {
      onDrop();
    },
  });

  // Combine refs for the row
  const setRowRef = (node: HTMLTableRowElement | null) => {
    previewRef(node);
    dropRef(node);
  };

  // Attach drag ref to handle
  dragRef(dragHandleRef);

  const renderCell = (column: DataTableColumn) => {
    if (column.options?.render) {
      return column.options.render(row);
    }
    if (column.options?.renderLite) {
      return column.options.renderLite(row[column.key]);
    }
    return row[column.key];
  };

  const stickyFirstClasses = stickyFirstColumn
    ? 'first:tw-sticky first:tw-left-0 first:tw-z-10'
    : '';

  const tdClassName = [
    'tw-py-2 tw-px-2',
    'tw-border-b tw-border-x-0 tw-border-border tw-border-solid',
    isFirstRow ? 'tw-border-t' : 'tw-border-t-0',
    'first:tw-pl-4 last:tw-pr-4 first:tw-border-l last:tw-border-r',
    stickyFirstClasses,
    isSelected ? 'first:!tw-bg-muted' : 'first:tw-bg-card',
    isFirstRow ? 'first:tw-rounded-tl-lg last:tw-rounded-tr-lg' : '',
    isLastRow ? 'first:tw-rounded-bl-lg last:tw-rounded-br-lg' : '',
  ].filter(Boolean).join(' ');

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      e.stopPropagation();
      onRowClick(row);
    }
  };
  const isSelectedClassName = isSelected
    ? 'tw-bg-gray-50'
    : 'tw-bg-card';

  const rowClassName = [
    'tw-table-row',
    isSelectedClassName,
    onRowClick ? ' tw-cursor-pointer hover:tw-bg-gray-50' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <tr
      ref={setRowRef}
      className={rowClassName}
      aria-selected={isSelected}
      onClick={handleRowClick}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {columns.map((column) => {
        // Check if this is the drag handle column
        if (column.key === 'drag_handle' && dragEnabled) {
          return (
            <td
              key={column.key}
              id={`drag-handle-${index}`}
              className={`${tdClassName} ${column?.className || ''}`}
              style={{
                width: getSize(column?.options?.size, columns),
              }}
            >
              <div
                ref={dragHandleRef}
                className="drag-handle tw-cursor-grab tw-flex tw-items-center
                tw-justify-center tw-text-gray-400 hover:tw-text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Icon type="drag_handle" />
              </div>
            </td>
          );
        }

        return (
          <td
            className={`${tdClassName} ${column?.className || ''}`}
            key={column.key}
            style={{
              width: getSize(column?.options?.size, columns),
            }}
          >
            <div
              className={`tw-flex tw-flex-row tw-items-center tw-gap-2 ${getAlign(
                column.options?.align
              )}`}
            >
              {renderCell(column)}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

export default DraggableTableRow;
